import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Locale, Translation } from 'locale'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import {
  AuthorisedUser,
  clientActionAdminLogsOutUser,
  CrudActions,
  CrudEventEnum,
  EntityTypeEnum,
  LoginStatus,
  User,
  UserDetail,
  UserStatus,
  WorkflowActions
} from 'api'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { ActionsButtonProps } from 'view/components/datagrid'
import { UserDialog } from '../UserDialog'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { getUserFullName } from 'model'

export interface UserActionsButtonProps extends ModalConsumerProps, ActionsButtonProps<UserDetail> {
  authorisedUser: AuthorisedUser
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  onShowUserEntityDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector()
})

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog,
  onShowUserEntityDialog: WorkflowActions.createEditWorkflow
}

export class UserActionsButtonComponent extends React.Component<UserActionsButtonProps> {
  render() {
    const viewItems = this.props.items.filter(item => !this.canEditDetails(item))
    const editItems = this.props.items.filter(item => this.canEditDetails(item))
    const suspendItems = this.props.items.filter(item => this.canSuspend(item))
    const activateItems = this.props.items.filter(item => this.canActivate(item))
    const disableItems = this.props.items.filter(item => this.canDisable(item))
    const enableItems = this.props.items.filter(item => this.canEnable(item))
    const logoutItems = this.props.items.filter(item => this.canSignOut(item))

    const userActions: ActionItem[] = [
      {
        label: this.getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
        onClick: () => this.handleItemDialog(viewItems, true),
        enabled: viewItems.length === 1
      },
      {
        label: this.getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
        onClick: () => this.handleItemDialog(editItems, false),
        enabled: editItems.length === 1
      },
      {
        label: this.getLabel(Translation.TC_ACTION_SUSPEND_TRADING, suspendItems.length),
        onClick: () => this.handleSuspend(suspendItems),
        enabled: suspendItems.length > 0
      },
      {
        label: this.getLabel(Translation.TC_ACTION_ACTIVATE_TRADING, activateItems.length),
        onClick: () => this.handleActivate(activateItems),
        enabled: activateItems.length > 0
      },
      {
        label: this.getLabel(Translation.TC_ACTION_DISABLE, disableItems.length),
        onClick: () => this.handleDisable(disableItems),
        enabled: disableItems.length > 0
      },
      {
        label: this.getLabel(Translation.TC_ACTION_ENABLE, enableItems.length),
        onClick: () => this.handleEnable(enableItems),
        enabled: enableItems.length > 0
      },
      {
        label: this.getLabel(Translation.TC_ACTION_SIGN_OUT_USER, logoutItems.length),
        onClick: () => this.handleSignOut(logoutItems),
        enabled: logoutItems.length > 0
      }
    ]

    return <SplitButtonDropdown actions={userActions} maxButtons={1} />
  }

  private getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = this.props
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  private handleItemDialog = (items: UserDetail[], readMode: boolean) => {
    if (readMode) {
      return this.props.showModal(<UserDialog userEntityId={items[0].user.id} readMode={true} workflowId={''} />)
    }
    const correlatedResponseCallback = (workflowId: string) => {
      this.props.showModal(<UserDialog workflowId={workflowId} userEntityId={items[0].user.id} readMode={false} />)
    }
    return this.props.onShowUserEntityDialog(EntityTypeEnum.USER, items[0].user.id, correlatedResponseCallback)
  }

  private handleSuspend = (suspendItems: UserDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

    const messageText =
      suspendItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_USER, { name: getUserFullName(suspendItems[0].user) })
        : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_USER, { count: suspendItems.length })

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: this.getBulkUpdateAction(suspendItems, { canTrade: false })
      }
    })
  }

  private handleActivate = (activateItems: UserDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

    const messageText =
      activateItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_USER, { name: getUserFullName(activateItems[0].user) })
        : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_USER, { count: activateItems.length })

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: this.getBulkUpdateAction(activateItems, { canTrade: true })
      }
    })
  }

  private handleDisable = (disableItems: UserDetail[]) => {
    const titleText =
      disableItems.length === 1
        ? Locale.translate(Translation.TC_PROMPT_TITLE_DISABLE_SINGLE_USER)
        : Locale.translate(Translation.TC_PROMPT_TITLE_DISABLE_MULTI_USER)

    const messageText =
      disableItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_DISABLE_SINGLE_USER, { name: getUserFullName(disableItems[0].user) })
        : Locale.translate(Translation.SC_PROMPT_MSG_DISABLE_MULTI_USER, { count: disableItems.length })

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: this.getBulkUpdateAction(disableItems, { status: UserStatus.SUSPENDED })
      }
    })
  }

  private handleEnable = (enableItems: UserDetail[]) => {
    const titleText =
      enableItems.length === 1
        ? Locale.translate(Translation.TC_PROMPT_TITLE_ENABLE_SINGLE_USER)
        : Locale.translate(Translation.TC_PROMPT_TITLE_ENABLE_MULTI_USER)

    const messageText =
      enableItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ENABLE_SINGLE_USER, { name: getUserFullName(enableItems[0].user) })
        : Locale.translate(Translation.SC_PROMPT_MSG_ENABLE_MULTI_USER, { count: enableItems.length })

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: this.getBulkUpdateAction(enableItems, { status: UserStatus.ACTIVE })
      }
    })
  }

  private handleSignOut = (logoutItems: UserDetail[]) => {
    const getBulkAdminLogsOutUserAction = (items: UserDetail[]) => {
      return items.map(item =>
        clientActionAdminLogsOutUser({
          usernameBeingLoggedOut: item.user.username
        })
      )
    }

    const titleText =
      logoutItems.length === 1
        ? Locale.translate(Translation.TC_PROMPT_TITLE_SIGN_OUT_SINGLE_USER)
        : Locale.translate(Translation.TC_PROMPT_TITLE_SIGN_OUT_MULTI_USER)

    const messageText =
      logoutItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SIGN_OUT_SINGLE_USER, { name: getUserFullName(logoutItems[0].user) })
        : Locale.translate(Translation.SC_PROMPT_MSG_SIGN_OUT_MULTI_USER, { count: logoutItems.length })

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: getBulkAdminLogsOutUserAction(logoutItems)
      }
    })
  }

  private getBulkUpdateAction = (items: UserDetail[], partial: Partial<User>) => {
    // TODO bulk operations
    return items.map(item =>
      CrudActions.userUpsert({
        ...item.user,
        ...partial
      })
    )
  }

  private canEditDetails = (userDetail: UserDetail) => userDetail.availableEvents.includes(CrudEventEnum.UPSERT)

  private canSuspend(item: UserDetail) {
    return item.user.canTrade && item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canActivate(item: UserDetail) {
    return !item.user.canTrade && item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canDisable(item: UserDetail) {
    return (
      item.user.status !== UserStatus.SUSPENDED &&
      item.availableEvents.includes(CrudEventEnum.UPSERT) &&
      item.user.id !== this.props.authorisedUser.id
    )
  }

  private canEnable(item: UserDetail) {
    return item.user.status !== UserStatus.ACTIVE && item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canSignOut(item: UserDetail) {
    return item.user.loginStatus === LoginStatus.LOGGED_IN && item.availableEvents.includes(CrudEventEnum.UPSERT)
  }
}

export const UserActionsButton = connect(mapStateToProps, mapDispatchToProps)(withModal(UserActionsButtonComponent))
